import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AppShell from "../../components/layout/AppShell";
import Input from "../../components/ui/Input";
import OtpInput from "../../components/ui/OtpInput";
import PasswordInput from "../../components/ui/PasswordInput";
import Button from "../../components/ui/Button";
import { clearCredentials } from "../../store/authSlice";
import { sendOtp, changePassword } from "../../services/api";
import { getPasswordError } from "../../utils/password";

const ErrorBanner = ({ message }) =>
  message ? (
    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 mb-5 text-sm">
      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      {message}
    </div>
  ) : null;

// ─── Step indicator ────────────────────────────────────────────────────────────
const Steps = ({ current }) => (
  <div className="flex items-center gap-2 mb-6">
    {["Verify Identity", "Change Password"].map((label, i) => {
      const idx = i + 1;
      const active = current === idx;
      const done = current > idx;
      return (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                done
                  ? "bg-brand text-white"
                  : active
                  ? "bg-brand text-white"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              {done ? (
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : idx}
            </div>
            <span className={`text-sm ${active ? "font-semibold text-gray-900" : "text-gray-400"}`}>
              {label}
            </span>
          </div>
          {i < 1 && <div className="flex-1 h-px bg-gray-200 mx-1" />}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Change Password ────────────────────────────────────────────────────────────
const ChangePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedEmail = useSelector((s) => s.auth.email);
  const role = useSelector((s) => s.auth.role);

  const backPath = role === "receiver" ? "/receiver/overview" : "/sender/invoices";

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(storedEmail || "");
  const [hashId, setHashId] = useState("");
  const [otp, setOtp] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState("");

  // ── Step 1: Send OTP ─────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!email.trim()) errs.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = "Enter a valid email address.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setBannerError("");
    setLoading(true);
    try {
      const res = await sendOtp({ email, emailType: "otp" });
      const id = res?.data?.hashId || res?.data?.hashID || res?.hashId || res?.hashID;
      if (!id) throw new Error("Could not get verification token. Please try again.");
      setHashId(id);
      setStep(2);
      toast.success("OTP sent to your email.");
    } catch (err) {
      setBannerError(err?.response?.data?.message || err?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    setBannerError("");
    setResending(true);
    try {
      const res = await sendOtp({ email, emailType: "otp" });
      const id = res?.data?.hashId || res?.data?.hashID || res?.hashId || res?.hashID;
      if (!id) throw new Error("Could not resend OTP.");
      setHashId(id);
      setOtp("");
      toast.success("OTP resent.");
    } catch (err) {
      setBannerError(err?.response?.data?.message || err?.message || "Failed to resend OTP.");
    } finally {
      setResending(false);
    }
  };

  // ── Step 2: Change password ───────────────────────────────────────────────
  const handleChange = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!otp.trim() || otp.length < 4) errs.otp = "Enter the OTP sent to your email.";
    if (!oldPassword) errs.oldPassword = "Current password is required.";
    const passwordError = getPasswordError(newPassword);
    if (passwordError) errs.newPassword = passwordError;
    if (newPassword && oldPassword && newPassword === oldPassword)
      errs.newPassword = "New password must be different from current password.";
    if (!confirmPassword) errs.confirmPassword = "Please confirm your new password.";
    else if (newPassword !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setBannerError("");
    setLoading(true);
    try {
      await changePassword({ username: email, oldPassword, newPassword, hashId, otp });
      toast.success("Password changed. Please sign in again.");
      dispatch(clearCredentials());
      navigate("/auth/login");
    } catch (err) {
      setBannerError(err?.response?.data?.message || err?.message || "Failed to change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-[520px]">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Change Password</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Update your account password. You will be signed out after a successful change.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <Steps current={step} />

          {/* ── Step 1 ── */}
          {step === 1 && (
            <>
              <ErrorBanner message={bannerError} />
              <form onSubmit={handleSendOtp} noValidate>
                <div className="space-y-4">
                  <Input
                    label="Email address"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors({}); setBannerError(""); }}
                    placeholder="you@company.com"
                    hint="We'll send a One-time passcode to this email."
                    error={errors.email}
                    required
                  />
                </div>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(backPath)}
                    className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <div className="flex-1">
                    <Button type="submit" loading={loading}>Send OTP</Button>
                  </div>
                </div>
              </form>
            </>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <>
              <p className="text-sm text-gray-500 mb-4">
                A code was sent to{" "}
                <span className="font-medium text-gray-800">{email}</span>.
              </p>
              <ErrorBanner message={bannerError} />
              <form onSubmit={handleChange} noValidate>
                <div className="space-y-4">
                  {/* OTP */}
                  <div>
                    <OtpInput
                      value={otp}
                      onChange={(value) => { setOtp(value); setErrors((p) => ({ ...p, otp: "" })); }}
                      error={errors.otp}
                    />
                    <div className="mt-1 text-right">
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resending}
                        className="text-xs text-brand hover:underline disabled:opacity-50"
                      >
                        {resending ? "Sending…" : "Resend code"}
                      </button>
                    </div>
                  </div>

                  {/* Current password */}
                  <PasswordInput
                    label="Current password"
                    name="oldPassword"
                    value={oldPassword}
                    onChange={(e) => { setOldPassword(e.target.value); setErrors((p) => ({ ...p, oldPassword: "" })); }}
                    placeholder="Your current password"
                    error={errors.oldPassword}
                    required
                    autoComplete="current-password"
                    enforceMaxLength={false}
                  />

                  {/* New password */}
                  <PasswordInput
                    label="New password"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setErrors((p) => ({ ...p, newPassword: "" })); }}
                    placeholder="Enter new password"
                    error={errors.newPassword}
                    required
                    showRequirements
                  />

                  {/* Confirm new password */}
                  <PasswordInput
                    label="Confirm new password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirmPassword: "" })); }}
                    placeholder="Re-enter new password"
                    error={errors.confirmPassword}
                    required
                  />
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep(1); setBannerError(""); setErrors({}); setOtp(""); }}
                    className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back
                  </button>
                  <div className="flex-1">
                    <Button type="submit" loading={loading}>Change Password</Button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default ChangePassword;
